import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Dropdown } from 'antd';
import theme from '../theme';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Icon from '../Icon';
import Input from '../Input';
import Space from '../Space';
import { Menu, MenuItem, MenuDivider } from '../Menu';
import { Text } from '../Typography';
import { FilteredMlsOptionsType } from './types';

type MlsFilterDropwdonwProps = {
  mlsOptions: { label: string; value: string }[];
  filteredMlsOptions: FilteredMlsOptionsType;
  setFilteredMlsOptions: React.Dispatch<FilteredMlsOptionsType>;
  positionRelative?: boolean;
};

const MlsFilterDropdown: React.FC<MlsFilterDropwdonwProps> = ({
  mlsOptions,
  filteredMlsOptions,
  setFilteredMlsOptions,
  positionRelative,
}) => {
  const [visible, setVisible] = useState(false);
  const [filterText, setFilterText] = useState('All MLS');
  const [searchText, setSearchText] = useState('');
  const [tempFilteredMlsOptions, setTempFilteredMlsOptions] = useState({
    ...filteredMlsOptions,
  });

  const handleReset = useCallback(() => {
    const options = Object.keys(filteredMlsOptions).reduce(
      (acc: any, curr: string) => {
        acc[curr] = false;
        return acc;
      },
      {},
    );
    setFilteredMlsOptions(options);
  }, [filteredMlsOptions, setFilteredMlsOptions]);

  const handleOk = useCallback(() => {
    setFilteredMlsOptions({ ...tempFilteredMlsOptions });
    setVisible(false);
  }, [setFilteredMlsOptions, setVisible, tempFilteredMlsOptions]);

  useEffect(() => {
    const count = Object.keys(filteredMlsOptions).length;
    const selectedCount =
      Object.values(filteredMlsOptions).filter(Boolean).length;
    if (!selectedCount || count === selectedCount) {
      setFilterText('All MLS');
    } else {
      setFilterText(`Selected - ${selectedCount}`);
    }
    setTempFilteredMlsOptions({ ...filteredMlsOptions });
  }, [filteredMlsOptions]);

  const overlay = useMemo(() => {
    const mlsOptionsOrSearch = mlsOptions.filter(({ label }) =>
      label.toLowerCase().includes(searchText.toLowerCase()),
    );
    return (
      <StyledMlsMenu>
        <StyledMlsMenuItem key={'search'}>
          <Input
            data-cy="input-mls-filter-search"
            placeholder="Search"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            suffix={<Icon type="search" color="gray5" />}
          />
        </StyledMlsMenuItem>
        <StyledMlsMenuItem key={'options'}>
          <StyledMlsOptions direction="vertical">
            {mlsOptionsOrSearch.map(({ label, value }) => (
                <Checkbox
                  data-cy={`checkbox-mls-filter-${value.toLowerCase()}`}
                  key={value}
                  checked={tempFilteredMlsOptions[value]}
                  onChange={e =>
                    setTempFilteredMlsOptions({
                      ...tempFilteredMlsOptions,
                      [value]: e.target.checked,
                    })
                  }
                >
                  {label}
                </Checkbox>
              ))}
            {!mlsOptionsOrSearch.length && (
              <Text type="secondary" data-cy="text-no-results">
                No results found
              </Text>
            )}
          </StyledMlsOptions>
        </StyledMlsMenuItem>
        <MenuDivider />
        <StyledMlsMenuItem key={'clear-apply'}>
          <StyledButtonWrapper>
            <Button
              data-cy="button-mls-filter-reset"
              type="link"
              onClick={handleReset}
              disabled={
                !Object.values(tempFilteredMlsOptions).filter(Boolean).length
              }
            >
              Clear
            </Button>
            <Button
              data-cy="button-mls-filter-ok"
              type="primary"
              onClick={handleOk}
            >
              Apply
            </Button>
          </StyledButtonWrapper>
        </StyledMlsMenuItem>
      </StyledMlsMenu>
    );
  }, [
    tempFilteredMlsOptions,
    handleOk,
    handleReset,
    mlsOptions,
    searchText,
    setTempFilteredMlsOptions,
  ]);

  return (
    <StyledDropdown
      $positionRelative={positionRelative}
      dropdownRender={() => overlay}
      open={visible}
      {...(!positionRelative && { getPopupContainer: el => el.parentElement })}
    >
      <Button
        data-cy="button-mls-filter-dropdown"
        onClick={() => setVisible(!visible)}
      >
        {filterText}{' '}
        <Icon
          type="antdDownOutlined"
          width={10}
          color={'black'}
          offset={[0, -3]}
        />
      </Button>
    </StyledDropdown>
  );
};

export default MlsFilterDropdown;

const StyledDropdown = styled(Dropdown)<{ $positionRelative?: boolean }>`
  ${({ $positionRelative }) =>
    !$positionRelative &&
    `
  position: absolute;
  top: 53px;
  right: 12px;
  `}
`;

const StyledMlsMenu = styled(Menu)`
  padding: 12px;
  width: 180px;

  > .ant-dropdown-menu-item {
    padding: 0;
    height: auto;
    &:hover {
      background-color: ${theme.gray1};
    }
    > .ant-divider {
      margin: 0;
      padding: 0;
    }
  }
`;

const StyledMlsMenuItem = styled(MenuItem).attrs({
  hoverable: false,
})`
  &&& .ant-dropdown-menu-title-content > * {
    padding: 5px 0px;
    display: block;
  }
`;

const StyledMlsOptions = styled(Space)`
  overflow-y: auto;
  max-height: 20vh;
  width: 100%;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;
